import { useState, useMemo, useRef } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useVirtualizer } from "@tanstack/react-virtual";

const ITEM_HEIGHT = 36;
const LIST_MAX_HEIGHT = 240;

/**
 * Generic searchable select (combobox).
 *
 * Props:
 *   options           — array of { value: string|number, label: string }
 *   value             — currently selected value (string|number|null)
 *   onChange          — callback(value)
 *   placeholder       — trigger placeholder text (default: "Select…")
 *   searchPlaceholder — input placeholder (default: "Search…")
 *   className         — extra classes on the trigger button
 *   testId            — data-testid for the trigger
 *   virtualized       — enable windowed rendering for large lists (default: false)
 */
export function SearchableSelect({
  options = [],
  value,
  onChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  className,
  testId,
  virtualized = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const parentRef = useRef(null);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const selected = options.find(
    (o) => String(o.value) === String(value),
  );

  const rowVirtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ITEM_HEIGHT,
    overscan: 8,
    enabled: virtualized && open,
  });

  const handleSelect = (opt) => {
    onChange(opt.value);
    setSearch("");
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal",
            !selected && "text-muted-foreground",
            className,
          )}
          data-testid={testId}
        >
          <span className="truncate">{selected ? selected.label : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={search}
            onValueChange={setSearch}
          />

          {virtualized ? (
            <div>
              {filtered.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  No results found.
                </p>
              ) : (
                <div
                  ref={parentRef}
                  style={{ height: Math.min(filtered.length * ITEM_HEIGHT, LIST_MAX_HEIGHT), overflow: "auto" }}
                >
                  <div style={{ height: rowVirtualizer.getTotalSize(), position: "relative" }}>
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                      const opt = filtered[virtualRow.index];
                      const isSelected = String(value) === String(opt.value);
                      return (
                        <button
                          key={opt.value}
                          onClick={() => handleSelect(opt)}
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: `${virtualRow.size}px`,
                            transform: `translateY(${virtualRow.start}px)`,
                          }}
                          className={cn(
                            "flex items-center gap-2 px-3 text-sm text-left hover:bg-accent hover:text-accent-foreground cursor-pointer",
                            isSelected && "bg-accent/50",
                          )}
                        >
                          <Check
                            className={cn(
                              "h-4 w-4 shrink-0",
                              isSelected ? "opacity-100" : "opacity-0",
                            )}
                          />
                          <span className="truncate">{opt.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <CommandList className="max-h-60 overflow-y-auto">
              {filtered.length === 0 && (
                <CommandEmpty>No results found.</CommandEmpty>
              )}
              {filtered.map((o) => (
                <CommandItem
                  key={o.value}
                  value={String(o.value)}
                  onSelect={() => handleSelect(o)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4 shrink-0",
                      String(value) === String(o.value) ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {o.label}
                </CommandItem>
              ))}
            </CommandList>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
